// Copyright (c) Docugami, Inc. All rights reserved.

import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { TextEntryDialog } from "components/shell/utils/TextEntryDialog";
import { useWaitCursor } from "hooks/WaitCursor";
import { useState } from "react";
import { useGetLabelQualityControlSettingsQuery, useUpdateLabelQualityControlSettingsMutation, useDeleteLabelQualityControlUserDescriptionMutation } from "store/frontend/frontendSlice";
import { QualityControlCancelledSchema, QualityControlDescriptionConfirmedSchema, QualityControlDescriptionRevertedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";


interface LabelQualityControlDialogProps {
    docsetId: string;
    labelId: string;
    onClose: () => void;
}

export const LabelQualityControlDialog = (props: LabelQualityControlDialogProps): JSX.Element => {
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const { data: settings, isLoading: queryLoading } = useGetLabelQualityControlSettingsQuery({docsetId: props.docsetId, labelId: props.labelId});
    const [triggerUpdate, updateStatus] = useUpdateLabelQualityControlSettingsMutation();
    const [triggerDelete, deleteStatus] = useDeleteLabelQualityControlUserDescriptionMutation();
    useWaitCursor(updateStatus.isLoading || deleteStatus.isLoading || queryLoading);

    async function confirm(value: string, toggle: boolean) {
        Telemetry.trackEvent<QualityControlDescriptionConfirmedSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlDescriptionConfirmed
        });

        value = value.trim();

        const enabled = !!toggle;
        const newDescription = !!value && value !== settings?.description;
        const newEnabled = enabled !== !!settings?.enabled;
        let closeDialog = true;

        if (newDescription || newEnabled) {
            if (newDescription || (enabled && newEnabled)) {
                setConfirmationVisible(true);
                closeDialog = false;
            }

            await triggerUpdate({
                labelId: props.labelId,
                docsetId: props.docsetId,
                description: value,
                enabled: toggle})
        }

        if (closeDialog) {
            props.onClose();
        }
    }

    function dismiss() {
        Telemetry.trackEvent<QualityControlCancelledSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlCancelled
        });
        props.onClose();
    }

    async function revert() {
        Telemetry.trackEvent<QualityControlDescriptionRevertedSchema>({
            docSetId: props.docsetId,
            labelId: props.labelId,
            eventName: TelemetryEvent.QualityControlDescriptionReverted
        });
        await triggerDelete({docsetId: props.docsetId, labelId: props.labelId});
        props.onClose();
    }

    function validateInput(value: string, toggle: boolean): boolean {
        const trimmed = value.trim();
        if (trimmed.length > 2000) { 
            return false; 
         }
         
         const textEmpty = trimmed.length === 0;
         const textChanged = trimmed !== settings?.description?.trim();
         const enableStateChanged = !!toggle !== !!settings?.enabled;
         return (textChanged && !textEmpty) || enableStateChanged;
    }

    return (
        queryLoading ? <></> :
        <>
            <TextEntryDialog
                hidden={false}
                originalText={!!settings?.description ? settings?.description : ''}
                placeholderText={!!!settings?.description ? "A description has not yet been generated.  It will be generated after more instances have been confirmed." : "Enter a descriptions, or use the Revert button to use the system generated description."}
                title="Quality Control Settings"
                description="The following description will be used by quality control when reviewing answers for this label.  You can enable or disable quality control for this label with the toggle below."
                multiline={true}
                rows={4}
                width="800px"
                confirmButtonText="Update"
                dismissButtonText="Close"
                revertButtonText={!!settings?.isUser ? "Revert" : undefined}
                toggle={{ onText: "Enabled", offText: "Disabled", checked: !!settings?.enabled, confirmedCallback: (value, toggle) => confirm(value, toggle), confirmedButtonDisabled: (value, toggle) => !validateInput(value, toggle) }}
                dismissedCallback={() => dismiss()}
                revertCallback={() => revert()}
                actionInProgress={updateStatus.isLoading || deleteStatus.isLoading}/>
            <ConfirmationDialog
                hidden={!confirmationVisible}
                title=""
                subText="New settings will take effect for future answers."
                confirmButtonText="OK"
                dismissedCallback={() => { setConfirmationVisible(false); props.onClose(); }}
                confirmedCallback={() => { setConfirmationVisible(false); props.onClose(); }} />
        </>
    );
}
